import React, { useMemo } from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import {
  safeNum,
  safeArray,
  formatInt,
  ratioToPct,
} from '../../../utils/gpsFormUtils';
import { DONUT_MAX_MODALITIES } from '../../../constants/gpsForms';
import { VIOLET } from './constants';
import ExplorerNotice from './ExplorerNotice';
import DeltaCell from './DeltaCell';
import InfoTooltip from '../../ui/InfoTooltip';

const DONUT_COLORS = [VIOLET, '#C4B5FD', '#A78BFA', '#9CA3AF', '#D1D5DB', '#E5E7EB'];

const CUMUL_NOTE = 'Plusieurs réponses possibles : le cumul des pourcentages peut dépasser 100 %.';

const QUESTION_RESPONDENTS_TOOLTIP =
  'Nombre de tickets distincts ayant répondu à cette question précise. En choix multiple, un ticket n\'est compté qu\'une fois même s\'il coche plusieurs réponses.';

const ChoiceMultichoice = ({ breakdown, comparePrevious, denominator }) => {
  const answerType = breakdown?.answerType;
  const current =
    breakdown?.current && typeof breakdown.current === 'object' ? breakdown.current : {};
  const previous =
    breakdown?.previous && typeof breakdown.previous === 'object' ? breakdown.previous : {};
  const modalities = safeArray(current.modalities);
  const prevModalities = safeArray(previous.modalities);
  const prevByValue = useMemo(() => {
    const map = new Map();
    prevModalities.forEach((m) => {
      if (m && m.value != null) map.set(m.value, m);
    });
    return map;
  }, [prevModalities]);

  const selections = safeNum(current.selections);
  const respondents = safeNum(current.respondents);
  const isMulti = answerType === 'multichoice';
  const useSelectionsDenom = isMulti && denominator === 'selections';

  if (modalities.length === 0) {
    return (
      <ExplorerNotice>
        Aucune réponse pour cette question sur la période et les filtres sélectionnés.
      </ExplorerNotice>
    );
  }

  // ≤6 modalities + single choice → donut; otherwise horizontal bars.
  const donutData = modalities.map((m) => ({
    name: String(m?.value ?? 'Non spécifié'),
    value: safeNum(m?.respondents),
  }));
  const donutTotal = donutData.reduce((sum, item) => sum + safeNum(item.value), 0);
  // Guard: an all-zero (but non-empty) payload would make donut divide by 0 → fall through to bars.
  const useDonut =
    answerType === 'choice' &&
    modalities.length <= DONUT_MAX_MODALITIES &&
    !comparePrevious &&
    donutTotal > 0;

  if (useDonut) {
    return (
      <div data-testid="explorer-donut">
        {/* Header */}
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          <span className="font-semibold text-gray-900 dark:text-white">
            {formatInt(respondents)}
          </span>{' '}
          répondants
          <InfoTooltip text={QUESTION_RESPONDENTS_TOOLTIP} className="ml-1" />{' '}
          • % des répondants
        </p>

        {/* Donut + legend side-by-side */}
        <div className="flex items-start gap-6">
          {/* Donut */}
          <div className="relative flex-shrink-0 w-[180px] h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={donutData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  innerRadius={52}
                  label={false}
                >
                  {donutData.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={DONUT_COLORS[index % DONUT_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [
                    `${formatInt(value)} (${ratioToPct(safeNum(value) / donutTotal)}%)`,
                    name,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Center label — sits inside the donut hole */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums leading-tight">
                {formatInt(respondents)}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 leading-tight">
                répondants
              </span>
            </div>
          </div>

          {/* Legend */}
          <div
            className="flex-1 min-w-0 space-y-2 pt-1 max-h-[70vh] overflow-y-auto gi-scrollbar gi-scrollbar--thin"
            tabIndex={0}
            aria-label="Liste des modalités"
          >
            {donutData.map((item, index) => {
              const pct = ratioToPct(safeNum(item.value) / donutTotal);
              const color = DONUT_COLORS[index % DONUT_COLORS.length];
              return (
                <div key={item.name} className="flex items-center gap-2 min-w-0">
                  <span
                    className="flex-shrink-0 w-3 h-3 rounded-sm"
                    style={{ backgroundColor: color }}
                  />
                  <span className="flex-1 min-w-0 text-sm text-gray-700 dark:text-gray-300 truncate">
                    {item.name}
                  </span>
                  <span className="flex-shrink-0 text-sm tabular-nums">
                    <span className="font-semibold text-gray-900 dark:text-white">{pct}&nbsp;%</span>
                    <span className="text-gray-500 dark:text-gray-400 ml-1">
                      ({formatInt(item.value)})
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Horizontal bars (multichoice always; choice >6 or when comparing N-1).
  return (
    <div data-testid="explorer-bars">
      {/* Header */}
      {isMulti && (
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
          <span className="font-semibold text-gray-900 dark:text-white">
            {formatInt(selections)}
          </span>{' '}
          sélections ·{' '}
          <span className="font-semibold text-gray-900 dark:text-white">
            {formatInt(respondents)}
          </span>{' '}
          répondants
          <InfoTooltip text={QUESTION_RESPONDENTS_TOOLTIP} className="ml-1" />
        </p>
      )}

      {/* Column captions */}
      <div
        className={`flex items-center text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2 ${
          comparePrevious ? 'pr-[5.5rem]' : ''
        }`}
      >
        <span className="flex-1">Élément</span>
        <span className="w-24 text-right mr-3">%</span>
        {comparePrevious && <span className="w-20 text-right">Δ vs N-1</span>}
      </div>

      {/* Rows */}
      <div
        className="space-y-3 max-h-[70vh] overflow-y-auto gi-scrollbar gi-scrollbar--thin"
        tabIndex={0}
        aria-label="Liste des modalités"
      >
        {modalities.map((m, idx) => {
          const value = String(m?.value ?? 'Non spécifié');
          const count = useSelectionsDenom ? safeNum(m?.selections) : safeNum(m?.respondents);
          const pct = useSelectionsDenom
            ? ratioToPct(m?.pctOfSelections)
            : ratioToPct(m?.pctOfRespondents);
          const prevM = prevByValue.get(m?.value);
          return (
            <div key={`${value}-${idx}`} className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-left text-gray-800 dark:text-gray-200 truncate mb-1" title={value}>
                  {value}
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                  <div
                    className="h-2.5 rounded-full"
                    style={{ width: `${Math.min(100, pct)}%`, backgroundColor: VIOLET }}
                    role="progressbar"
                    aria-valuenow={pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
              </div>
              {/* % + count */}
              <div className="w-24 flex-shrink-0 text-right leading-tight">
                <span className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                  {pct} %
                </span>
                <br />
                <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                  ({formatInt(count)})
                </span>
              </div>
              {/* Δ column */}
              {comparePrevious && (
                <div className="w-20 flex-shrink-0 text-right" data-testid="explorer-delta">
                  <DeltaCell
                    curPctOfRespondents={m?.pctOfRespondents}
                    prevPctOfRespondents={prevM?.pctOfRespondents}
                    isNew={m?.isNew}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Cumul footer */}
      {isMulti && (
        <p className="mt-4 flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <svg
            className="flex-shrink-0 mt-0.5 h-3.5 w-3.5"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
              clipRule="evenodd"
            />
          </svg>
          {CUMUL_NOTE}
        </p>
      )}
    </div>
  );
};

export default ChoiceMultichoice;
